import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';

import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  open: boolean;
  workerId: number;
  existingCertificateIds?: number[];
  onClose: () => void;
  onBadgeAdded: () => void;
};

interface CatalogCertificate {
  id: number;
  name: string;
  display_name_en: string | null;
  certificate_type: 'BADGE' | 'CERTIFICATE';
}

const errorMessageFrom = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
  return fallback;
};

const AddWorkerBadgeModal = ({
  open,
  workerId,
  existingCertificateIds = [],
  onClose,
  onBadgeAdded,
}: Props) => {
  const [certificates, setCertificates] = useState<CatalogCertificate[]>([]);
  const [selected, setSelected] = useState<CatalogCertificate | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // How many records the catalog holds in total, so "nothing to assign" can
  // distinguish an empty catalog from a worker who already holds everything.
  const [catalogTotal, setCatalogTotal] = useState(0);

  useEffect(() => {
    if (!open) return;

    const fetchCatalog = async () => {
      try {
        setLoading(true);
        setError(null);
        // Reset first: a failed refetch must not leave the previous open's
        // options on screen next to an error message.
        setCertificates([]);
        setCatalogTotal(0);

        // Follow the pagination rather than reading page 1 and stopping. The
        // old `per_page: 100` silently truncated the catalog — an agency with
        // more than 100 records simply could not grant the ones past the cut,
        // and nothing said so. PAGE_CAP bounds the loop so a bad `total_pages`
        // cannot spin forever.
        const PER_PAGE = 100;
        const PAGE_CAP = 20;
        const all: CatalogCertificate[] = [];
        let page = 1;
        let totalPages = 1;
        let total = 0;

        do {
          // eslint-disable-next-line no-await-in-loop
          const response = await axiosRubyClient.get(
            '/api/admin/certificates',
            {
              params: { per_page: PER_PAGE, page },
            },
          );
          all.push(...(response.data.data || []));
          totalPages = response.data.pagination?.total_pages ?? 1;
          total = response.data.pagination?.total ?? all.length;
          page += 1;
        } while (page <= totalPages && page <= PAGE_CAP);

        setCatalogTotal(total);
        setCertificates(
          all.filter((cert) => !existingCertificateIds.includes(cert.id)),
        );
      } catch (err) {
        setCertificates([]);
        setError(errorMessageFrom(err, 'Failed to load the badge catalog.'));
      } finally {
        setLoading(false);
      }
    };

    fetchCatalog();
    // `existingCertificateIds` is a fresh array each render; keying the fetch
    // on `open` alone avoids an infinite refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    if (!selected) return;

    try {
      setSubmitting(true);
      setError(null);
      await axiosRubyClient.post(
        `/api/admin/workers/${workerId}/certificates`,
        { certificate_id: selected.id },
      );
      setSelected(null);
      onBadgeAdded();
      onClose();
    } catch (err) {
      setError(errorMessageFrom(err, 'Failed to assign the badge.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelected(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      aria-labelledby="assign-badge-title"
      open={open}
      maxWidth="sm"
      onClose={handleClose}
    >
      {/* MUI does not wire DialogTitle to the dialog automatically, so without
          aria-labelledby a screen reader announces an unnamed dialog. */}
      <DialogTitle id="assign-badge-title">Assign a badge</DialogTitle>
      <DialogContent>
        <Stack vertical gap={16} style={{ paddingTop: 8 }}>
          {loading ? (
            <Stack justify="center">
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <Autocomplete
              fullWidth
              // Everything in the catalog is offered, and certificates are
              // suffixed "(certificate)" — so the field is not badge-only and
              // must not say it is.
              noOptionsText={
                catalogTotal === 0
                  ? 'The catalogue has no badges or certificates yet.'
                  : 'This worker already holds every badge and certificate in the catalogue.'
              }
              getOptionLabel={(option) =>
                `${option.display_name_en || option.name}${
                  option.certificate_type === 'BADGE' ? '' : ' (certificate)'
                }`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={certificates}
              value={selected}
              renderInput={(params) => (
                <TextField {...params} autoFocus label="Badge or certificate" />
              )}
              onChange={(_event, value) => setSelected(value)}
            />
          )}

          <Small color="lighter">
            Granting a badge lets this worker be matched to and claim shifts
            that require it. Only badges can be set as a client requirement.
          </Small>

          {error && <Small color="danger">{error}</Small>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          disabled={!selected || submitting}
          variant="contained"
          onClick={handleSubmit}
        >
          {submitting ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddWorkerBadgeModal;
